import { createSlice } from "@reduxjs/toolkit";
import { isRoleDisabled } from "constants/authRoles";
import { loadStoredTimezone, setDisplayTimezone, DEFAULT_TIMEZONE } from "utils/datetime";

const getSystemColorMode = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const initialTimezone = loadStoredTimezone();
setDisplayTimezone(initialTimezone);

const initialState = {
  mode: getSystemColorMode(),
  user: null, // effective user (role overridden when viewing as)
  realUser: null, // actual logged-in user from /auth/me
  viewAsRole: null, // caller | lead
  timezone: initialTimezone,
  twoFactorRequired: false,
};

const VIEW_AS_STORAGE_KEY = "viewAsRole";
const VIEW_AS_ROLES = ["caller"].filter((role) => !isRoleDisabled(role));

export const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    setMode: (state) => {
      state.mode = state.mode === "light" ? "dark" : "light";
    },

    setTimezone: (state, action) => {
      const tz = action.payload || DEFAULT_TIMEZONE;
      state.timezone = tz;
      setDisplayTimezone(tz);
    },

    setUser: (state, action) => {
      const payload = action.payload;
      state.realUser = payload;
      state.twoFactorRequired = !!(payload && payload.twoFactorRequired);

      // Re-apply a saved "view as" role on reload (admins/superadmins only).
      let saved = null;
      try {
        saved = sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
      } catch (_) {
        /* ignore */
      }

      const canViewAs = payload && ["admin", "superadmin"].includes(payload.role);
      if (canViewAs && saved && VIEW_AS_ROLES.includes(saved)) {
        state.viewAsRole = saved;
        state.user = { ...payload, role: saved };
      } else {
        state.viewAsRole = null;
        state.user = payload;
      }
    },

    setTwoFactorRequired: (state, action) => {
      state.twoFactorRequired = !!action.payload;
      if (state.user) {
        state.user = { ...state.user, twoFactorRequired: !!action.payload, twoFactorVerified: !action.payload };
      }
      if (state.realUser) {
        state.realUser = {
          ...state.realUser,
          twoFactorRequired: !!action.payload,
          twoFactorVerified: !action.payload,
        };
      }
    },

    setViewAsRole: (state, action) => {
      const role = action.payload;
      if (!state.realUser) return;

      if (role && VIEW_AS_ROLES.includes(role)) {
        state.viewAsRole = role;
        state.user = { ...state.realUser, role };
        try {
          sessionStorage.setItem(VIEW_AS_STORAGE_KEY, role);
        } catch (_) {
          /* ignore */
        }
      } else {
        state.viewAsRole = null;
        state.user = { ...state.realUser };
        try {
          sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
        } catch (_) {
          /* ignore */
        }
      }
    },

    restoreViewAsRole: (state) => {
      if (!state.realUser || !["admin", "superadmin"].includes(state.realUser.role)) return;
      let saved = null;
      try {
        saved = sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
      } catch (_) {
        /* ignore */
      }
      if (saved && ["caller", "lead", "member"].includes(saved)) {
        state.viewAsRole = saved;
        state.user = { ...state.realUser, role: saved };
      }
    },

    logoutUser: (state) => {
      state.user = null;
      state.realUser = null;
      state.viewAsRole = null;
      state.twoFactorRequired = false;
      try {
        sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
      } catch (_) {
        /* ignore */
      }
    },
  },
});

export const {
  setMode,
  setTimezone,
  setUser,
  setViewAsRole,
  restoreViewAsRole,
  logoutUser,
  setTwoFactorRequired,
} = globalSlice.actions;

export default globalSlice.reducer;
